import { useState, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";

interface RcvSyncState {
  isChecking: boolean;
  isSyncing: boolean;
  needsCredentials: boolean;
  lastJobId: string | null;
}

interface UseRcvSyncResult extends RcvSyncState {
  triggerSync: (periodoTributario: string, rut: string) => Promise<void>;
  saveCredentialsAndSync: (
    siiWebRut: string,
    siiWebPassword: string,
    periodoTributario: string,
    companyRut: string,
  ) => Promise<void>;
  dismissCredentialsModal: () => void;
}

export function useRcvSync(): UseRcvSyncResult {
  const [state, setState] = useState<RcvSyncState>({
    isChecking: false,
    isSyncing: false,
    needsCredentials: false,
    lastJobId: null,
  });

  const doSync = useCallback(async (periodoTributario: string, rut: string) => {
    setState((s) => ({ ...s, isSyncing: true }));
    try {
      const result = await apiFetch.post<{ jobId: string }>("/api/rcv/sync", {
        periodoTributario,
        rut,
      });
      setState((s) => ({ ...s, isSyncing: false, lastJobId: result.jobId }));
    } catch {
      setState((s) => ({ ...s, isSyncing: false }));
      throw new Error("Error al iniciar sincronización");
    }
  }, []);

  const triggerSync = useCallback(
    async (periodoTributario: string, rut: string) => {
      // Pre-flight: check if credentials exist
      setState((s) => ({ ...s, isChecking: true }));
      try {
        const { hasSiiWebCredentials } = await apiFetch.get<{
          hasSiiWebCredentials: boolean;
        }>("/api/rcv/sync/preflight");

        if (!hasSiiWebCredentials) {
          setState((s) => ({ ...s, isChecking: false, needsCredentials: true }));
          return;
        }
      } catch {
        setState((s) => ({ ...s, isChecking: false }));
        throw new Error("Error al verificar credenciales");
      }

      setState((s) => ({ ...s, isChecking: false }));
      await doSync(periodoTributario, rut);
    },
    [doSync],
  );

  const saveCredentialsAndSync = useCallback(
    async (
      siiWebRut: string,
      siiWebPassword: string,
      periodoTributario: string,
      companyRut: string,
    ) => {
      // Save credentials via tax-authority endpoint
      await apiFetch.post("/api/tax-authority", { siiWebRut, siiWebPassword });

      setState((s) => ({ ...s, needsCredentials: false }));

      // Now trigger the sync
      await doSync(periodoTributario, companyRut);
    },
    [doSync],
  );

  const dismissCredentialsModal = useCallback(() => {
    setState((s) => ({ ...s, needsCredentials: false }));
  }, []);

  return {
    ...state,
    triggerSync,
    saveCredentialsAndSync,
    dismissCredentialsModal,
  };
}
